

































































































import Vue from 'vue';
import { Toast } from '@/components/ds';
import LoadingPage from '@/components/LoadingPage.vue';
import ProuniFileUpload from '@/components/forms/prouni/ProuniFileUpload.vue';
import ProuniOtherDocuments from '@/components/forms/prouni/ProuniOtherDocuments.vue';
import ProuniHeader from '@/components/prouni/ProuniHeader.vue';
import { getAssetPath } from '@/helpers/assets';
import {
  ProuniApplication,
  ProuniApplicationStatus,
  ProuniDocument,
  ProuniDocumentStatus,
  ProuniDocumentType,
} from '@/store/modules/prouni/types';

// Canonical document-type order used to sort the list within each subject group.
const DOCUMENT_TYPE_ORDER: string[] = [
  ProuniDocumentType.inscriptionForm,
  ProuniDocumentType.identity,
  ProuniDocumentType.ctps,
  ProuniDocumentType.cpf,
  ProuniDocumentType.guardian,
  ProuniDocumentType.residenceProof,
  ProuniDocumentType.ownActivityProof,
  ProuniDocumentType.ownIncomeProofMonth1,
  ProuniDocumentType.ownIncomeProofMonth2,
  ProuniDocumentType.ownIncomeProofMonth3,
  ProuniDocumentType.alimonyProof,
  ProuniDocumentType.highSchoolCertificate,
  ProuniDocumentType.highSchoolTranscript,
  ProuniDocumentType.teacherProof,
  ProuniDocumentType.disabilityReport,
  ProuniDocumentType.familyMemberId,
  ProuniDocumentType.familyMemberResidenceProof,
  ProuniDocumentType.familyMemberActivityProof,
  ProuniDocumentType.familyMemberIncomeProofMonth1,
  ProuniDocumentType.familyMemberIncomeProofMonth2,
  ProuniDocumentType.familyMemberIncomeProofMonth3,
];

export default Vue.extend({
  name: 'ProuniAcompanhamento',
  components: { LoadingPage, ProuniFileUpload, ProuniOtherDocuments, ProuniHeader, Toast },
  data() {
    return {
      isLoading: false,
      error: false,
      documentStatus: ProuniDocumentStatus,
      heroImageSrc: getAssetPath('img/prouni/hero-followup.svg'),
    };
  },
  computed: {
    application(): ProuniApplication | null {
      return this.$store.getters.prouniApplication;
    },
    documents(): ProuniDocument[] {
      return this.$store.getters.prouniDocuments;
    },
    // Grouped by subject (candidate first, then each family member in listing
    // order) and, within each group, ordered by document type.
    sortedDocuments(): ProuniDocument[] {
      const members = (this.application && this.application.familyMembers) || [];
      const memberRank = new Map<number, number>();
      members.forEach((member, index) => {
        if (member.id != null) memberRank.set(member.id, index);
      });
      const groupRank = (doc: ProuniDocument): number =>
        doc.familyMemberId ? memberRank.get(doc.familyMemberId) ?? 999 : -1;
      const typeRank = (doc: ProuniDocument): number => {
        const index = DOCUMENT_TYPE_ORDER.indexOf(doc.documentType);
        return index < 0 ? 999 : index;
      };
      // Free-form ("other") documents are managed in their own section below.
      return [...this.documents]
        .filter(doc => doc.documentType !== ProuniDocumentType.other)
        .sort((a, b) => groupRank(a) - groupRank(b) || typeRank(a) - typeRank(b));
    },
    status(): string {
      return (this.application && this.application.status) || ProuniApplicationStatus.draft;
    },
    isApproved(): boolean {
      return this.status === ProuniApplicationStatus.approved;
    },
    isRejected(): boolean {
      return this.status === ProuniApplicationStatus.rejected;
    },
    hasRejectedDocuments(): boolean {
      return this.documents.some(d => d.status === ProuniDocumentStatus.rejected);
    },
    // When a document is rejected the application moves to "awaiting" (aguardando
    // reenvio); surface a top-level attention banner asking for a re-upload. Also
    // guards the legacy "submitted + rejected doc" case, but never overrides an
    // admin-terminal approved/rejected application.
    showAttention(): boolean {
      return this.hasRejectedDocuments && !this.isApproved && !this.isRejected;
    },
  },
  mounted() {
    this.refresh();
  },
  methods: {
    // Appends the family member's name to documents tied to a member so
    // multiple members' documents can be told apart.
    documentLabel(doc: ProuniDocument): string {
      const base = this.$t(`prouni.documentTypes.${doc.documentType}`) as string;
      if (!doc.familyMemberId) return base;
      const members = (this.application && this.application.familyMembers) || [];
      const member = members.find(m => m.id === doc.familyMemberId);
      return member ? `${base} — ${member.name}` : base;
    },
    async refresh() {
      this.isLoading = true;
      this.error = false;
      try {
        await this.$store.dispatch('GET_MY_PROUNI_APPLICATION');
      } catch (err) {
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },
  },
});
